import { CHECKOUT } from '../../constants/checkout/checkout.constant';
import { getCartData } from '../cart/im-cart.service';
import { ImCommonReplaceProducts } from '../common/im-common.helper';
import { getCurrencyCode } from '../data/im-data.helper';
import { imEventPageView } from '../event/im-event.helper';
import { getStoreData, getContextData, getUserData, getOrderData, getEventData, getPageData } from '../object-data/im-object-data.helper';
import { getItemInfoCart } from '../product-data/im-product-data.service';

import { Cart } from '@api/cart/cart.models';
import { GeolocationDetail } from '@api/common/models/geolocation.model';
import { WalletCard } from '@api/payment/payment.model';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EcommerceContext } from '@core/domain/models/context.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { PublicUser } from '@features/user';
import { AnalitycsEvent, CustomData } from '@shared/analytics/models/analytics/analytics.models';
import { CheckoutDataObject } from '@shared/analytics/models/checkout/checkout.models';
import { EventData, PageData, ProductData, YesOrNoTypeString } from '@shared/analytics/models/general/general.models';
import { PageViewDataObject } from '@shared/analytics/models/page-view/page-view.models';

export const imCheckoutCreatePageName = (
    title: string,
    step: number,
    window: CommonWindow,
    itemsCartNumber?: number,
    url?: string,
    countryCode?: string,
): PageData => {
    const section: string = 'checkout';
    let context: PageData;

    if (step === 0) {
        context = getPageData(window, url, section, 'cesta', title, countryCode);
    } else if (step === 1 && itemsCartNumber === 0) {
        context = getPageData(window, url, section, 'cesta', title, countryCode);
    } else if (step === 1 && itemsCartNumber !== 0) {
        context = getPageData(window, url, section, 'cesta', title, countryCode);
    } else if (step === 2) {
        context = getPageData(window, url, section, 'envio', title, countryCode);
    } else if (step === 3) {
        context = getPageData(window, url, section, 'pago', title, countryCode);
    } else if (step === 4) {
        context = getPageData(window, url, section, 'resumen_compra', title, countryCode);
    }
    return context;
};

export const imCheckoutSaveForLaterPageview = (
    title: string,
    step: number,
    context: EcommerceContext,
    user: PublicUser,
    window: CommonWindow
) => {
    const dataObject: PageViewDataObject = {
        page: imCheckoutCreatePageName(title, step, window, null, null, context.store.countryCode),
        context: getContextData(context, window),
        user: getUserData(user, window),
    };

    imEventPageView([dataObject], { ecommerce: undefined });
};

export const imCheckoutSelectWalletCard = (card: WalletCard): { eventData: EventData; customData: CustomData } => {
    const type: string = card ? card.paymentCodeName : '';

    const eventData: EventData = {
        cf: 'checkout',
        category: 'pago',
        action: 'seleccionar_pago_guardado',
        label: type.toLowerCase(),
    };

    const customData: CustomData = {
        order: {
            payment: {
                savedData: YesOrNoTypeString.si,
            },
        },
    };
    return { eventData, customData };
};

export const imCheckoutSendCheckout = (
    data: PageViewDataObject[],
    currencyCode: string,
    step: number,
    products: any[]
): void => {
    const ecommerceObject: CheckoutDataObject = CHECKOUT;
    const newProducts = ImCommonReplaceProducts(ecommerceObject.ecommerce.checkout.products, products);

    ecommerceObject.ecommerce = { ...ecommerceObject.ecommerce, currencyCode };
    ecommerceObject.ecommerce = {
        ...ecommerceObject.ecommerce,
        checkout: { actionField: { step: step.toString() }, products: newProducts },
    };
    imEventPageView(data, ecommerceObject);
};

export const imCheckoutCreateShopCart = (
    shopCart: Cart,
    title: string,
    step: number,
    user: PublicUser,
    context: EcommerceContext,
    window: CommonWindow,
    storageService: CoreStorageService,
    url?: string,
    isFastSint?: boolean
): void => {
    let itemsCartNumber: number = 0;
    if (typeof shopCart !== 'undefined' && 'items' in shopCart) {
        itemsCartNumber = shopCart.items.length;
    }
    const dataObject: PageViewDataObject = {
        cart: getCartData(shopCart.items, false),
        store: getStoreData(null),
        page: imCheckoutCreatePageName(title, step, window, itemsCartNumber, url, context.store.countryCode),
        context: getContextData(context, window, isFastSint),
        user: getUserData(user, window),
    };
    // dimension 27 para fastSint
    dataObject.context.clickCollect = isFastSint ? YesOrNoTypeString.si : YesOrNoTypeString.no;

    switch (title) {
        case 'lista_de_productos':
        case 'datos_envio':
        case 'datos_pago':
            // eslint-disable-next-line no-case-declarations
            const productInfo: ProductData[] = shopCart.items.map((item) => {
                return getItemInfoCart(
                    item,
                    context.store.details.currencyInfo,
                    storageService,
                    shopCart,
                    null
                );
            });
            imCheckoutSendCheckout(
                [dataObject],
                getCurrencyCode(
                    context.store.details.currencyInfo
                ),
                step,
                productInfo
            );
            break;
        default:
            imEventPageView([dataObject], { ecommerce: undefined });
            break;
    }
};

export const imCheckoutCreateSummary = (
    shopCart: Cart,
    store: GeolocationDetail,
    user: PublicUser,
    context: EcommerceContext,
    window: CommonWindow,
    storageService: CoreStorageService,
    currencyInfo: CurrencyInfo,
    url?: string,
    isFastSint?: boolean
): void => {
    const title = 'datos_pedido';
    const dataObject: PageViewDataObject = {
        cart: getCartData(shopCart.items, false),
        order: getOrderData(shopCart, user, currencyInfo),
        user: getUserData(user, window),
        store: getStoreData(store),
        page: imCheckoutCreatePageName(title, 4, window, null, url, context.store.countryCode),
        context: getContextData(context, window),
    };
    // dimension 27 para fastSint
    dataObject.context.clickCollect = isFastSint ? YesOrNoTypeString.si : YesOrNoTypeString.no;
    const productInfo: ProductData[] = shopCart.items.map((item) => {
        return getItemInfoCart(item, context.store.details.currencyInfo, storageService, shopCart, null);
    });
    imCheckoutSendCheckout(
        [dataObject],
        getCurrencyCode(
            context.store.details.currencyInfo
        ),
        4,
        productInfo
    );
};

export const imCheckoutCheckoutEvent = (paymentData: any, shopCart: Cart, requireBill: boolean): AnalitycsEvent[] => {
    const events: AnalitycsEvent[] = [];
    const paymentType: string = paymentData.type.toLowerCase();

    const dataObject: AnalitycsEvent = {
        eventData: getEventData('checkout', 'resumen_compra', 'autorizar_pago', paymentType),
        customData: {
            order: {
                payment: {
                    giftTicket: shopCart.giftTicket ? YesOrNoTypeString.si : YesOrNoTypeString.no,
                },
            },
        },
    };
    events.push(dataObject);
    if (requireBill) {
        const newDataObject: AnalitycsEvent = {
            ...dataObject,
            eventData: getEventData('checkout', 'resumen_compra', 'solicitar_factura', null),
        };
        events.push(newDataObject);
    }
    return events;
};

export const imCheckoutSendCheckoutBackEvent = (destination: string): EventData => {
    const eventData: EventData = {
        cf: 'checkout',
        category: 'envio',
        action: 'volver_atras',
        label: destination,
    };

    return eventData;
};

export const imCheckoutSendCheckoutSelectPlaceEvent = (payload: {
    placeId: string;
    shippingMethodKind: string;
    source: 'list' | 'map';
}): AnalitycsEvent => {
    const type = payload.shippingMethodKind === 'droppoint' ? 'droppoint' : 'tienda';
    const source = payload.source === 'list' ? 'lista' : 'mapa';
    const cf = payload.shippingMethodKind === 'droppoint' ? 'localizador_droppoint' : 'localizador_tiendas';

    const eventData: EventData = {
        cf,
        category: 'envio',
        action: `seleccionar_${type}_${source}`,
        label: payload.placeId,
    };

    const customData: CustomData = payload.shippingMethodKind === 'pickup'
        ? {
            store: {
                storeId: payload.placeId,
            },
        }
        : {};

    const data: AnalitycsEvent = { eventData, customData };

    return data;
};

export const imCheckoutSendCheckoutConfirmPlaceEvent = (payload: {
    placeId: string;
    shippingMethodKind: string;
}): AnalitycsEvent => {
    const type = payload.shippingMethodKind === 'droppoint' ? 'droppoint' : 'tienda';

    const cf = payload.shippingMethodKind === 'droppoint' ? 'localizador_droppoint' : 'localizador_tiendas';

    const eventData: EventData = {
        cf,
        category: 'envio',
        action: `seleccionar_${type}`,
        label: payload.placeId,
    };

    const storeData = type === 'droppoint' ? { dropPoint: payload.placeId } : { storeId: payload.placeId };

    const customData: CustomData = {
        store: {
            ...storeData,
        },
    };

    const data: AnalitycsEvent = { eventData, customData };

    return data;
};

export const imCheckoutCheckoutNextStepEvent = (activePath): EventData => {
    const paths = {
        shipping: 'envio',
        payment: 'pago',
        summary: 'resumen_compra',
    };

    const eventData: EventData = {
        cf: 'checkout',
        category: paths[activePath],
        action: 'siguiente_paso',
    };

    return eventData;
};

export const imCheckoutOpenPurchaseConditions = (): EventData => {
    const eventData: EventData = getEventData('legal', 'resumen_compra', 'ver_condiciones_compra', null);

    return eventData;
};

export const imCheckoutOpenPrivacyPolicy = (): EventData => {
    const eventData: EventData = getEventData('legal', 'resumen_compra', 'ver_politica_privacidad', null);

    return eventData;
};

export const imCheckoutCheckoutBillingDataOk = (): EventData => {
    const eventData: EventData = getEventData('checkout', 'resumen_compra', 'datos_facturacion_ok', null);

    return eventData;
};
